import React from 'react';
import { useLazyGetNotificationPreferencesQuery, useUpdatePreferenceMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';

export type NotificationPreference = { field: string; value: boolean };

export function useNotificationPreferences() {
  const [getPrefs, { data, isFetching }] = useLazyGetNotificationPreferencesQuery();
  const [updatePref, { isLoading: isUpdating }] = useUpdatePreferenceMutation();
  const [preferences, setPreferences] = React.useState<NotificationPreference[]>([]);
  const { showSuccess, showError } = useToast();

  React.useEffect(() => {
    getPrefs();
  }, [getPrefs]);

  React.useEffect(() => {
    const list = (data as any)?.response as NotificationPreference[] | undefined;
    if (Array.isArray(list)) setPreferences(list);
  }, [data]);

  const getValue = React.useCallback((key: string) => !!preferences.find((p) => p.field === key)?.value, [preferences]);

  const setValueLocal = React.useCallback((key: string, next: boolean) => {
    setPreferences((prev) => {
      const existing = prev.find((p) => p.field === key);
      if (existing) return prev.map((p) => (p.field === key ? { ...p, value: next } : p));
      return [...prev, { field: key, value: next }];
    });
  }, []);

  const togglePreference = React.useCallback(async (key: string, next: boolean, channel: string = 'push') => {
    // Optimistic update
    setValueLocal(key, next);
    try {
      const fd = new FormData();
      fd.append('field', key);
      fd.append('value', String(next));
      fd.append('channel', channel);
      await updatePref(fd).unwrap();
      showSuccess('Preference saved');
    } catch (error: unknown) {
      // Revert on failure and avoid throwing non-Error objects to the runtime overlay
      setValueLocal(key, !next);
      const message = typeof error === 'string'
        ? error
        : (error as any)?.data?.message || (error as any)?.error || 'Failed to update notification preference';
      // Surface a readable message for debugging without crashing the app
      // eslint-disable-next-line no-console
      console.error('[NotificationPreferences] Update failed:', message, error);
      showError(message);
    }
  }, [setValueLocal, updatePref, showSuccess, showError]);

  return { preferences, isFetching, isUpdating, getValue, togglePreference } as const;
}


